import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { TabHeader } from '../components/TabHeader';
import { Footer } from '../components/Footer';

/**
 * PRD §8.2 bullet 4 — "Prefer a form?" alternative to the mailto CTAs.
 * Both paths route to the same sales inbox. Sprint 3 ships this as a
 * client-side form that opens the visitor\'s mail client with a pre-
 * filled body (no back-end required, no PII stored, no third-party
 * form provider). Sprint 5 can optionally add a Cloudflare Worker
 * POST target so the visitor never leaves the site — deferred until
 * we have the /api/ask Worker infrastructure in place anyway.
 */
export function PricingContactPage(): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-sovereign-900">
      <TabHeader />
      <main className="flex-1 px-4 py-10 md:px-8 md:py-14">
        <div className="mx-auto max-w-2xl">
          <nav aria-label="Breadcrumb" className="mb-3 text-xs text-ink-dim">
            <Link to="/" className="hover:text-brand-purple hover:underline">
              Home
            </Link>
            <span className="mx-2" aria-hidden>·</span>
            <Link to="/pricing" className="hover:text-brand-purple hover:underline">
              Pricing
            </Link>
            <span className="mx-2" aria-hidden>·</span>
            <span>Contact</span>
          </nav>

          <h1 className="mb-2 text-2xl font-bold text-ink md:text-3xl">
            Contact sales
          </h1>
          <p className="mb-6 text-sm text-ink-mute">
            Everything on this form ends up at{' '}
            <a
              href="mailto:sales@dideck.com"
              className="text-brand-purple hover:underline"
            >
              sales@dideck.com
            </a>
            . We reply within one business day with a proposal for a short qualification
            call. Nothing you enter here is stored by DI Deck — the form opens your mail
            client with your entries pre-filled so you send from your own address.
          </p>

          <ContactForm />
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ContactForm(): JSX.Element {
  const [service, setService] = useState<'cloud' | 'on-prem' | 'unsure'>('cloud');
  const [org, setOrg] = useState('');
  const [headcount, setHeadcount] = useState('');
  const [sector, setSector] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');
  const [timeline, setTimeline] = useState('');
  const [notes, setNotes] = useState('');

  function submit(e: FormEvent): void {
    e.preventDefault();
    const serviceLabel =
      service === 'cloud'
        ? 'Cloud service pilot enquiry'
        : service === 'on-prem'
          ? 'On-premise service pilot enquiry'
          : 'Pilot enquiry — service tier to be decided';
    const lines = [
      `Interested in a tailored ${service === 'unsure' ? '' : service + ' '}pilot of DI Deck. Details below.`,
      '',
      `Organisation: ${org}`,
      `Headcount band: ${headcount}`,
      `Sector: ${sector}`,
      `Primary jurisdiction: ${jurisdiction}`,
      `Preferred pilot start timeline: ${timeline}`,
      '',
      `Notes:`,
      notes,
      '',
      '(Sent from https://dideck.com/pricing/contact)',
    ];
    const params = new URLSearchParams({
      subject: `DI Deck — ${serviceLabel}`,
      body: lines.join('\n'),
    });
    window.location.href = `mailto:sales@dideck.com?${params.toString().replace(/\+/g, '%20')}`;
  }

  return (
    <form onSubmit={submit} className="space-y-4 rounded-md border border-sovereign-line bg-white p-6 shadow-card">
      <Field label="Interested in">
        <select
          value={service}
          onChange={(e) => setService(e.target.value as 'cloud' | 'on-prem' | 'unsure')}
          className="w-full rounded border border-sovereign-line bg-white px-3 py-2 text-sm text-ink"
        >
          <option value="cloud">Cloud service pilot</option>
          <option value="on-prem">On-premise service pilot</option>
          <option value="unsure">Not sure yet — happy to talk it through</option>
        </select>
      </Field>

      <Field label="Organisation" required>
        <input
          type="text"
          required
          value={org}
          onChange={(e) => setOrg(e.target.value)}
          className="w-full rounded border border-sovereign-line bg-white px-3 py-2 text-sm text-ink"
        />
      </Field>

      <Field label="Headcount band">
        <select
          value={headcount}
          onChange={(e) => setHeadcount(e.target.value)}
          className="w-full rounded border border-sovereign-line bg-white px-3 py-2 text-sm text-ink"
        >
          <option value="">Choose one…</option>
          <option>Under 100</option>
          <option>100 – 500</option>
          <option>500 – 2000</option>
          <option>2000 – 10 000</option>
          <option>10 000+</option>
        </select>
      </Field>

      <Field label="Sector">
        <input
          type="text"
          placeholder="e.g. Financial services, Defence, Public healthcare"
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          className="w-full rounded border border-sovereign-line bg-white px-3 py-2 text-sm text-ink"
        />
      </Field>

      <Field label="Primary jurisdiction">
        <select
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          className="w-full rounded border border-sovereign-line bg-white px-3 py-2 text-sm text-ink"
        >
          <option value="">Choose one…</option>
          <option>EU</option>
          <option>UK</option>
          <option>US</option>
          <option>APAC</option>
          <option>MENA</option>
          <option>Other / multiple</option>
        </select>
      </Field>

      <Field label="Preferred pilot start timeline">
        <select
          value={timeline}
          onChange={(e) => setTimeline(e.target.value)}
          className="w-full rounded border border-sovereign-line bg-white px-3 py-2 text-sm text-ink"
        >
          <option value="">Choose one…</option>
          <option>This quarter</option>
          <option>Next quarter</option>
          <option>H1 next year</option>
          <option>Exploring — no timeline yet</option>
        </select>
      </Field>

      <Field label="Anything specific to share up-front">
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full rounded border border-sovereign-line bg-white px-3 py-2 text-sm text-ink"
        />
      </Field>

      <div className="flex flex-col gap-2 pt-2">
        <button
          type="submit"
          className="rounded-md bg-brand-purple px-4 py-2.5 text-sm font-bold text-white shadow-card hover:bg-brand-purple-dark"
        >
          Open mail client with pre-filled message
        </button>
        <div className="text-center text-[11px] text-ink-dim">
          Sends from your own address to sales@dideck.com — nothing is stored by DI Deck.
        </div>
      </div>
    </form>
  );
}

function Field({
  label,
  required = false,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-ink">
        {label}
        {required && <span className="ml-1 text-brand-purple">*</span>}
      </span>
      {children}
    </label>
  );
}
